import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IndianRupee,
  TrendingUp,
  RefreshCw,
  ArrowLeft,
  AlertTriangle,
  ChevronRight,
  Layers,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';

import {
  UtilizationGauge,
  TrendLineChart,
} from '../components/charts';

import { YearDropdown, PhaseDropdown } from '../components/filters';

import { useGetExpenditureActivityTreeQuery } from '../services/api';

import type {
  Head,
  Subhead,
  HeadCode,
  MonthlyTrendData,
  ExpenditureActivityTreeNode,
} from '../types';
import { DEFAULT_HEADS } from '../types';

const HeadAnalyticsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { headCode } = useParams<{ headCode: string }>();
  const carouselImages = [img1];

  // State
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Get current year for display (fallback to current fiscal year)
  const currentYear = selectedYear || new Date().getFullYear();

  // Fetch expenditure activity tree from API (provides sanTotal, spentTotal, sanRec, sanNonRec, spentRec, spentNonRec)
  const { data: expenseTreeData, isLoading, refetch } = useGetExpenditureActivityTreeQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(selectedPhase !== null && { phase: selectedPhase }),
  });

  // Find the current head from API data
  const headFromAPI = useMemo(() => {
    if (!expenseTreeData?.success || !expenseTreeData?.data?.tree) return null;
    
    // headCode is like "H1", "H2" - extract the number
    const headNumber = parseInt(headCode?.replace('H', '') || '1');
    
    // Find the head by matching id or by index
    const foundHead = expenseTreeData.data.tree.find((h: ExpenditureActivityTreeNode, idx: number) => h.id === headNumber || idx === headNumber - 1) || null;
    
    return foundHead;
  }, [expenseTreeData, headCode]);

  // Transform API head data to Head type
  const head = useMemo<Head | null>(() => {
    if (!headFromAPI) return null;

    const colorArray = ['#6B1786', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'];
    const headIndex = expenseTreeData?.data?.tree?.findIndex((h: ExpenditureActivityTreeNode) => h.id === headFromAPI.id) ?? 0;
    const defaultHead = DEFAULT_HEADS.find(h => h.code === headCode) || DEFAULT_HEADS[0];

    // Use actual values from Expenditure API
    const sanctioned = headFromAPI.sanTotal || 0;
    const spent = headFromAPI.spentTotal || 0;
    const utilization = sanctioned > 0 ? (spent / sanctioned) * 100 : 0;

    // Count subheads - all direct children of a head should be subheads
    const subheadCount = headFromAPI.children?.length || 0;
    
    return {
      id: headFromAPI.id,
      code: headCode as HeadCode,
      name: headFromAPI.title,
      description: defaultHead?.description || headFromAPI.title,
      color: colorArray[headIndex % colorArray.length],
      totalSanctioned: sanctioned,
      totalReceived: sanctioned, // Using sanctioned as received for this API
      totalSpent: spent,
      totalUnspent: sanctioned - spent,
      utilizationPercentage: utilization,
      targetUnits: 0,
      completedUnits: 0,
      efficiencyPercentage: 0,
      subheadCount: subheadCount,
    };
  }, [headFromAPI, headCode, expenseTreeData]);

  // Transform API subheads data to Subhead type
  const subheads = useMemo<Subhead[]>(() => {
    if (!headFromAPI || !headFromAPI.children) return [];

    // All direct children of a head are subheads
    return headFromAPI.children
      .map((subhead: ExpenditureActivityTreeNode, idx: number) => {
        // Use actual values from Expenditure API
        const sanctioned = subhead.sanTotal || 0;
        const spent = subhead.spentTotal || 0;
        const utilization = sanctioned > 0 ? (spent / sanctioned) * 100 : 0;

        return {
          id: subhead.id,
          headId: headFromAPI.id,
          headCode: headCode as HeadCode,
          headName: headFromAPI.title,
          code: `${headCode}.${idx + 1}`,
          name: subhead.title,
          description: subhead.title,
          totalSanctioned: sanctioned,
          totalReceived: sanctioned, // Using sanctioned as received for this API
          totalSpent: spent,
          totalUnspent: sanctioned - spent,
          utilizationPercentage: utilization,
          targetUnits: 0,
          completedUnits: 0,
          efficiencyPercentage: 0,
          activityCount: subhead.children?.length || 0,
        };
      });
  }, [headFromAPI, headCode]);

  // Generate mock trends (API doesn't provide monthly data)
  const trends = useMemo<MonthlyTrendData[]>(() => {
    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    const sanctioned = head?.totalSanctioned || 0;
    
    return months.map((month, idx) => {
      const monthSanctioned = (sanctioned / 12) * (0.8 + Math.random() * 0.4);
      const monthReceived = monthSanctioned * (0.75 + Math.random() * 0.2);
      const monthUtilization = 40 + (idx * 4) + Math.random() * 10;
      return {
        month,
        year: idx < 9 ? currentYear : currentYear + 1,
        sanctioned: monthSanctioned,
        received: monthReceived,
        spent: monthReceived * (monthUtilization / 100),
        utilization: monthUtilization,
        recurringSpent: monthReceived * 0.4 * (monthUtilization / 100),
        nonRecurringSpent: monthReceived * 0.6 * (monthUtilization / 100),
      };
    });
  }, [head, currentYear]);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pm-shri')}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: head?.color }}
                >
                  {head?.code}
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{head?.name}</h1>
                  <p className="text-sm text-gray-500">{head?.description}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <YearDropdown
              value={selectedYear}
              onChange={setSelectedYear}
            />

            {/* Phase Selector */}
            <PhaseDropdown
              value={selectedPhase}
              onChange={setSelectedPhase}
            />

            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">Total Sanctioned</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{head?.totalSanctioned || 0}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-600">Total Spent</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(head?.totalSpent || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm text-gray-600">Unspent</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(head?.totalUnspent || 0)}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600 mb-2">Utilization</div>
                <div className="text-2xl font-bold" style={{ color: head?.color }}>
                  {head?.utilizationPercentage.toFixed(1)}%
                </div>
              </div>
              <UtilizationGauge value={head?.utilizationPercentage || 0} size="sm" showLabel={false} />
            </div>
          </div>
        </div>

        {/* Subheads Grid */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Subheads ({subheads.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subheads.map((subhead) => (
              <div
                key={subhead.id}
                onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}/subheads/${subhead.id}`)}
                className="p-4 rounded-lg border-2 cursor-pointer transition-all border-gray-200 hover:border-purple-400 hover:shadow-md hover:bg-purple-50"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-semibold text-gray-900">{subhead.name}</div>
                    <div className="text-xs text-gray-500">{subhead.code} • {subhead.activityCount} activities</div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-gray-500">Sanctioned</div>
                    <div className="font-semibold">{formatCurrency(subhead.totalSanctioned)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Spent</div>
                    <div className="font-semibold text-green-600">{formatCurrency(subhead.totalSpent)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Utilization</div>
                    <div className={`font-semibold ${
                      subhead.utilizationPercentage >= 75 ? 'text-green-600' :
                      subhead.utilizationPercentage >= 50 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {subhead.utilizationPercentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${subhead.utilizationPercentage}%`,
                      backgroundColor: head?.color,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Utilization Trend</h3>
          <TrendLineChart
            data={trends}
            height={350}
            showArea={true}
            dataKeys={['sanctioned', 'spent', 'utilization']}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HeadAnalyticsScreen;
